import Papa from 'papaparse';
import { QUESTIONS_META_URL } from '@config/constants';

interface QuestionMetaRow {
  question: string;
  soustitre: string;
  emoji: string;
}

export interface QuestionMeta {
  question: string;
  subtitle: string;
  emoji: string;
}

export class QuestionsMetaParser {
  private static questionsMetaMap: Map<string, QuestionMeta> = new Map();
  private static isLoaded = false;

  /**
   * Load questions metadata from CSV
   */
  static async loadQuestionsMeta(): Promise<void> {
    if (this.isLoaded) {
      console.log('📦 Questions metadata already loaded');
      return;
    }

    try {
      console.log('📥 Fetching questions metadata CSV from:', QUESTIONS_META_URL);
      
      // Utiliser le cache si disponible
      const CacheService = (await import('./cache-service')).CacheService;
      const response = await CacheService.fetchWithCache(QUESTIONS_META_URL);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const csvText = await response.text();

      const result = Papa.parse<QuestionMetaRow>(csvText, {
        header: true,
        skipEmptyLines: true,
      });

      // Clear and rebuild the map
      this.questionsMetaMap.clear();

      result.data.forEach(row => {
        if (row.question) {
          this.questionsMetaMap.set(row.question.trim(), {
            question: row.question.trim(),
            subtitle: row.soustitre?.trim() || '',
            emoji: row.emoji?.trim() || '💜',
          });
        }
      });

      this.isLoaded = true;
      console.log('✅ Questions metadata loaded:', this.questionsMetaMap.size, 'questions');
    } catch (error) {
      console.error('❌ Error loading questions metadata:', error);
      // Continue without metadata in case of error
      this.isLoaded = true;
    }
  }

  /**
   * Get metadata for a specific question
   */
  static getQuestionMeta(questionText: string): QuestionMeta | null {
    const trimmedQuestion = questionText.trim();
    return this.questionsMetaMap.get(trimmedQuestion) || null;
  }

  /**
   * Get emoji for a specific question
   */
  static getEmoji(questionText: string): string {
    const meta = this.getQuestionMeta(questionText);
    return meta?.emoji || '💜';
  }

  /**
   * Get subtitle for a specific question
   */
  static getSubtitle(questionText: string): string {
    const meta = this.getQuestionMeta(questionText);
    return meta?.subtitle || '';
  }

  /**
   * Reset metadata (useful for testing)
   */
  static reset(): void {
    this.questionsMetaMap.clear();
    this.isLoaded = false;
  }
}
